'use client';

import { Popover } from '@base-ui/react/popover';
import { useMediaQuery } from '@repo/hooks/useMediaQuery';
import { css } from '@repo/pandacss/styled-system/css';
import { up } from '@repo/utils/breakpoints';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { disclosureTooltipPopoverClassName } from './disclosureTooltipPopover';

export interface DisclosureTooltipProps {
  allowHoverInteraction?: boolean;
  anchorEl?: HTMLElement | null;
  children: ReactNode;
  handler: Popover.Handle<unknown>;
  keepMounted?: boolean;
  onOpen?: (open: boolean) => void;
  placement?:
    'top' | 'bottom' | 'left' | 'right' | 'top-start' | 'top-end' | 'bottom-start' | 'bottom-end';
  sideOffset?: number;
  tooltipClassName?: string;
  tooltipStyleOverrides?: React.CSSProperties;
  zIndex?: number;
}

const arrowStyles = css({
  width: '4',
  height: '4',
  transform: 'rotate(45deg)',
  backgroundColor: 'neutral.white',
  borderTopWidth: 'thin',
  borderRightWidth: 'thin',
  borderBottomWidth: 'thin',
  borderLeftWidth: 'thin',
  borderTopStyle: 'solid',
  borderRightStyle: 'solid',
  borderBottomStyle: 'solid',
  borderLeftStyle: 'solid',
  borderTopColor: 'neutral.lighter',
  borderRightColor: 'neutral.lighter',
  borderBottomColor: 'neutral.lighter',
  borderLeftColor: 'neutral.lighter',
  '&[data-side="top"]': {
    borderTopStyle: 'none',
    borderLeftStyle: 'none',
    bottom: '[0px]',
  },
  '&[data-side="bottom"]': {
    borderBottomStyle: 'none',
    borderRightStyle: 'none',
    top: '[0px]',
  },
  '&[data-side="left"]': {
    borderBottomStyle: 'none',
    borderLeftStyle: 'none',
    right: '[0px]',
  },
  '&[data-side="right"]': {
    borderTopStyle: 'none',
    borderRightStyle: 'none',
    left: '[0px]',
  },
  _before: {
    borderTopWidth: 'thin',
    borderRightWidth: 'thin',
    borderBottomWidth: 'thin',
    borderLeftWidth: 'thin',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: 'neutral.light',
    borderRightColor: 'neutral.light',
    borderBottomColor: 'neutral.light',
    borderLeftColor: 'neutral.light',
    backgroundColor: 'neutral.white',
  },
});

export function DisclosureTooltip({
  allowHoverInteraction = false,
  anchorEl,
  children,
  handler,
  keepMounted = false,
  onOpen,
  placement = 'bottom',
  sideOffset = 8,
  tooltipClassName = disclosureTooltipPopoverClassName,
  tooltipStyleOverrides,
  zIndex = 1300,
}: DisclosureTooltipProps) {
  const [open, setOpen] = useState(false);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const isSmAbove = useMediaQuery(up('sm'));

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      onOpen?.(nextOpen);
    },
    [onOpen],
  );

  const handleClose = useCallback(() => {
    handler.close();
  }, [handler]);

  // Click away listener
  useEffect(() => {
    if (!open) return;

    const handleClickAway = (event: MouseEvent) => {
      const target = event.target as Node;
      const triggerElement = anchorEl;
      const popoverElement = popoverRef.current;

      if (
        triggerElement &&
        !triggerElement.contains(target) &&
        popoverElement &&
        !popoverElement.contains(target)
      ) {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickAway);
    return () => document.removeEventListener('mousedown', handleClickAway);
  }, [open, anchorEl, handleClose]);

  const handleMouseLeave = () => {
    if (allowHoverInteraction) {
      handleClose();
    }
  };

  return (
    <Popover.Root handle={handler} onOpenChange={handleOpenChange}>
      <Popover.Portal keepMounted={keepMounted}>
        <Popover.Positioner
          anchor={anchorEl}
          side={placement.split('-')[0] as 'top' | 'bottom' | 'left' | 'right'}
          align={placement.includes('-') ? (placement.split('-')[1] as 'start' | 'end') : 'center'}
          sideOffset={sideOffset}
          collisionPadding={isSmAbove ? 16 : 0}
          style={{ zIndex }}
        >
          <Popover.Popup
            ref={popoverRef}
            className={tooltipClassName}
            onMouseEnter={() => {
              if (allowHoverInteraction) {
                setOpen(true);
              }
            }}
            onMouseLeave={handleMouseLeave}
            style={tooltipStyleOverrides}
          >
            <Popover.Arrow className={arrowStyles} />
            {children}
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover.Root>
  );
}
